import React, { useEffect, useState } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Utils
import clsx from 'clsx';

// Material
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';

// Material Icons
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

// Dropzone
import Dropzone from 'react-dropzone';

// SVG Inline
import SVG from 'react-inlinesvg';

// Components
import { ModalTemplate } from 'components/ModalTemplate';
import CloseIcon from 'components/Icons/Close';

// Constates
import CONST_IMAGE from 'constants/images';
import CONST_TRANSPORT_VOUCHER_COLLECTION from 'constants/transportVoucherCollectionReport';

// Types
import { ApplicationState } from 'store';
import { OrdersTypes } from 'store/ducks/orders/types';

// Actions
import { chargeRequestPayOrderRequest } from 'store/ducks/orders/actions';
import { setClose } from 'store/ducks/dialogs/actions';
import { dispatchActionsChainRequest } from 'store/ducks/utils/actions';

// Styles
import useStyles from './styles';

interface BatchManagementInterface {
  chargeRequestId?: string;
  title: string | string[];
  subtitle: string;
  page?: number;
  type: string;
}

type Props = BatchManagementInterface;

const BatchManagement: React.FC<Props> = ({
  chargeRequestId,
  title,
  subtitle,
  page,
  type,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);

  const [haveFile, setHaveFile] = useState<boolean>(false);
  const [files, setFiles] = useState<File[]>([]);

  const listingDrawerFactoryList = useSelector(
    (state: ApplicationState) => state.listingDrawer.factoryList,
  );

  const selectedCompanyStatus = useSelector(
    (state: ApplicationState) => state.companies.selectedStatus,
  );

  const selectedNSU = useSelector(
    (state: ApplicationState) => state.reports.selectedNSU,
  );

  const storegeLimit =
    JSON.parse(window.localStorage.getItem('persist:root') as string)
      ?.localLimit || '10';
  const { localLimit } = JSON.parse(storegeLimit) || 10;

  const onDropAccepted = (files: File[]) => {
    setFiles(files);
    setHaveFile(true);
  };

  const renderDragMessage = (isDragActive: boolean) => {
    if (!isDragActive) {
      return (
        <Box className={classes.uploadMessage}>
          <Typography>Arraste e solte seu arquivo aqui</Typography>
          <Typography>ou</Typography>
          <Typography className={classes.chooseFileText}>
            escolha no computador.
          </Typography>
        </Box>
      );
    }
    return <p className={classes.uploadMessage}>Solte o arquivo aqui</p>;
  };

  useEffect(
    () =>
      // Unmount
      () => {
        setHaveFile(false);
      },
    [],
  );

  useEffect(() => {
    if (listingDrawerFactoryList?.length > 0) {
      const available = ['CRIADO', 'EM PROCESSAMENTO'];
      let disabled = true;

      listingDrawerFactoryList.forEach((obj, index) => {
        if (available.includes(obj?.status || '')) {
          disabled = false;
        }
      });

      setIsButtonDisabled(disabled);
    }
  }, [listingDrawerFactoryList]);

  return (
    <ModalTemplate
      hideCloseIcon
      header={
        <Grid container className={classes.header}>
          <Typography className={classes.title}>
            {haveFile ? 'Verificando dados' : title}
          </Typography>

          <Grid item className={classes.closeIconContainer}>
            <IconButton
              onClick={() => {
                setHaveFile(false);
                dispatch(setClose());
              }}>
              <CloseIcon size='small' />
            </IconButton>
          </Grid>
        </Grid>
      }
      classes={{
        root: classes.resetPtop,
      }}>
      <Grid className={classes.gridContainer} item spacing={0}>
        <Grid
          item
          lg={12}
          md={12}
          sm={12}
          xl={12}
          xs={12}
          className={classes.textContainer}>
          <Grid container justifyContent='center'>
            <Grid item>
              <p className={classes.helpText}>{subtitle}</p>
            </Grid>
          </Grid>
        </Grid>

        <Grid item lg={12} md={12} sm={12} xl={12} xs={12}>
          <Dropzone disabled={haveFile} onDropAccepted={onDropAccepted}>
            {({
              getRootProps,
              getInputProps,
              isDragActive,
            }: {
              getRootProps: any;
              getInputProps: any;
              isDragActive: boolean;
            }) => (
              <Grid
                alignContent='center'
                container
                justifyContent='flex-start'
                spacing={0}
                {...getRootProps()}
                className={clsx({
                  [classes.dropzone]: !haveFile,
                  [classes.dropzoneCursor]: !haveFile,
                  [classes.dragActive]: isDragActive,
                })}>
                <input {...getInputProps()} />
                {haveFile && (
                  <Grid container className={classes.haveFile}>
                    <Grid container justifyContent='flex-end'>
                      <IconButton
                        onClick={() => {
                          setHaveFile(false);
                          setFiles([]);
                        }}>
                        <CloseIcon size='small' />
                      </IconButton>
                    </Grid>
                    <Grid container justifyContent='center'>
                      <Box width='100%' textAlign='center'>
                        <CloudUploadIcon />
                      </Box>
                      <Box width='100%' textAlign='center'>
                        <Typography>{files[0].name}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}
                {!haveFile && (
                  <Grid
                    container
                    alignItems='center'
                    className={classes.container}>
                    <Box
                      display='flex'
                      justifyContent='center'
                      alignItems='center'
                      padding='28% 0'>
                      <SVG
                        src={CONST_IMAGE.ICONS.UPLOAD}
                        title='upload arquivo check .xlsx'
                      />
                    </Box>

                    <Grid item>{renderDragMessage(isDragActive)}</Grid>
                  </Grid>
                )}
              </Grid>
            )}
          </Dropzone>
        </Grid>
      </Grid>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        marginTop={3}>
        <Box marginRight={2}>
          <Button
            className={clsx(classes.nextButton, classes.maintainButton)}
            onClick={() => {
              setHaveFile(false);
              dispatch(setClose());
            }}>
            cancelar
          </Button>
        </Box>
        <Button
          disabled={!chargeRequestId && isButtonDisabled}
          className={
            !chargeRequestId && isButtonDisabled
              ? classes.nextButtonDisabled
              : classes.nextButton
          }
          onClick={(e) => {
            e.preventDefault();

            const listOrdersParams = {
              type,
              status:
                selectedCompanyStatus !== ''
                  ? CONST_TRANSPORT_VOUCHER_COLLECTION.STATUS.find(
                      (s) => s.KEY === selectedCompanyStatus,
                    )?.KEY
                  : undefined,
              nsu: selectedNSU,
              _includes: 'payment,history,order',
              _order: 'DESC',
              _sort: 'createdAt',
              _limit: localLimit,
              page,
              isInstituition: true,
            };

            if (chargeRequestId) {
              dispatch(
                dispatchActionsChainRequest([
                  {
                    action: chargeRequestPayOrderRequest,
                    payload: {
                      body: {
                        chargeRequestId,
                        attachment: files,
                      },
                    },
                    typeSuccess: OrdersTypes.CHARGE_REQUEST_PAY_ORDER_SUCCESS,
                    typeFailure: OrdersTypes.CHARGE_REQUEST_PAY_ORDER_FAILURE,
                  },
                ]),
              );
            } else {
              const available = ['CRIADO', 'EM PROCESSAMENTO'];
              listingDrawerFactoryList.forEach((obj, index) => {
                if (available.includes(obj?.status || '')) {
                  dispatch(
                    chargeRequestPayOrderRequest({
                      body: {
                        chargeRequestId: obj.id,
                        attachment: files,
                      },
                      listOrdersParams,
                      isLastRequest:
                        index === listingDrawerFactoryList.length - 1,
                    }),
                  );
                }
              });
            }
          }}>
          Liberar
        </Button>
      </Box>
    </ModalTemplate>
  );
};

export { BatchManagement };
export default BatchManagement;
